import { useMutation, useQueryClient } from '@tanstack/react-query';
import { surveysQueryKeys } from '../Constants/queryKeys';
import { deleteSurveyRespondent } from '../Services/Surveys.service';
import { useActionToast } from '@/Hooks/useActionToast';

/**
 * Erase one person's answers to one survey (R27) — `DELETE /:id/responses/:platformUserId`.
 *
 * **It is not only a row leaving a list.** The response row is what makes somebody ineligible, so
 * removing it puts them back in the audience and the survey opens for them again, exactly as
 * `reset_viewer` does for a tour or an announcement. The confirmation on screen says both halves;
 * this hook is what makes the rest of the screen agree with them.
 *
 * Four caches go, and none of them is optional:
 *
 * - **responses**, every page and every status filter, because the deleted person is on one of them
 * - **results**, because the averages, "Enviaram" and the response rate counted that answer; a list
 *   that loses a row while the cards keep counting it produces numbers that lie quietly
 * - **comments**, because free text is read off the same responses
 * - **detail and lists**, because the card's `responseCount` and its own rate come from there
 *
 * All of them by **prefix**: the erasure crosses every version of the survey, so invalidating only
 * the version on screen would leave the others stale.
 */
export const useDeleteSurveyRespondent = (surveyId: string) => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: (platformUserId: number) => deleteSurveyRespondent(surveyId, platformUserId),
    onSuccess: result => {
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResponsesOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResultsOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allCommentsOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.detail(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.lists() });

      toast.success(
        `Resposta do usuário ${result.platformUserId} apagada`,
        result.deletedResponses > 1
          ? `Eram ${result.deletedResponses} respostas, de versões diferentes. A pesquisa volta a aparecer para essa pessoa no próximo acesso.`
          : 'A pessoa volta a ficar elegível e a pesquisa aparece para ela de novo no próximo acesso.',
      );
    },
    onError: (error: Error) => {
      /*
        The list is refetched on failure too, and that is not a contradiction of "never optimistic":
        nothing is removed here, the screen is put back in step with the BFF. It matters for the
        `404` — the row that is on screen because somebody else erased it while this page sat open
        is exactly the row a refetch takes away.
      */
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResponsesOf(surveyId) });
      toast.failure('Não foi possível apagar a resposta', error);
    },
  });
};
